import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useMatomo } from "./hooks";
import { MatomoService } from "./matomo-service";

/**
 * Helper to serialize search params
 */
function serializeSearch(search: unknown): string {
	if (typeof search === "string") {
		return search;
	}
	if (search && typeof search === "object") {
		const params = new URLSearchParams();
		Object.entries(search).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				params.set(key, String(value));
			}
		});
		const serialized = params.toString();
		return serialized ? `?${serialized}` : "";
	}
	return "";
}

/**
 * Matomo Route Tracker Component
 * Automatically tracks page views when routes change in TanStack Router
 */
export function MatomoRouteTracker() {
	const router = useRouter();
	const { trackPageView, isEnabled, hasConsent } = useMatomo();

	useEffect(() => {
		if (!isEnabled || !hasConsent) return;

		// Track initial page view
		const currentPath = router.state.location.pathname;
		const currentSearch = router.state.location.search;
		const currentUrl = `${window.location.origin}${currentPath}${serializeSearch(currentSearch)}`;

		trackPageView({
			documentTitle: document.title,
			href: currentUrl,
		});

		// Subscribe to route changes
		const unsubscribe = router.subscribe("onLoad", (event) => {
			// Track on successful navigation
			const newPath = event.toLocation.pathname;
			const newSearch = event.toLocation.search;
			const newUrl = `${window.location.origin}${newPath}${serializeSearch(newSearch)}`;

			// Wait a tick for the document title to update
			setTimeout(() => {
				trackPageView({
					documentTitle: document.title,
					href: newUrl,
				});
			}, 0);
		});

		return unsubscribe;
	}, [router, trackPageView, isEnabled, hasConsent]);

	// This component doesn't render anything
	return null;
}

/**
 * Hook to track route changes
 * Alternative to using the MatomoRouteTracker component
 */
export function useRouteTracking() {
	const router = useRouter();
	const { trackPageView, isEnabled, hasConsent } = useMatomo();

	useEffect(() => {
		if (!isEnabled || !hasConsent) return;

		// Function to track current location
		const trackCurrentLocation = () => {
			const location = router.state.location;
			const url = `${window.location.origin}${location.pathname}${serializeSearch(location.search)}`;
			const sanitizedUrl = MatomoService.sanitizeUrl(url);

			trackPageView({
				documentTitle: document.title,
				href: sanitizedUrl,
			});
		};

		// Track initial page view
		trackCurrentLocation();

		// Subscribe to route changes
		const unsubscribe = router.subscribe("onLoad", () => {
			// Wait for document title to update
			requestAnimationFrame(() => {
				trackCurrentLocation();
			});
		});

		return unsubscribe;
	}, [router, trackPageView, isEnabled, hasConsent]);
}
